import { definedMap, hasOwnProperty } from "collection-utils";

import { Icon } from "../components/icons";
import { DevStringKey, LocalizedStringKey } from "../components/localization";
import { SerializedComponent } from "../components/SerializedApp";
import { ActionSource } from "../components/types";
import { isTableName, TableName } from "../database-strings";
import { RemoteIcon } from "../remote-icon-type";
import { assert, assertNever, undefinedIfEmptyString } from "../Support";
import { ListItemAccessoryPosition, ListItemFlags } from "./array-screens/list-item-flags";
import { Appearance } from "./components/button-appearance";
import { ColumnStyle } from "./components/column-style";
import { ImageAspectRatio } from "./components/image-types";
import { Mood } from "./components/mood";
import {
    GlobalVariableNames,
    SpecialValueKind,
    TableColumn,
    TableGlideType,
    emailAuthScreenName,
    isComputedColumn,
} from "./description";

export type RowIndexOrRowID<T> =
    | { readonly rowIndex: T }
    // The `rowIndex` here is just the hint
    | { readonly keyColumnName: T; readonly keyColumnValue: T; readonly rowIndex?: T };

export abstract class ElementBuilder<T> {
    public abstract primitive(name: string, args: { [key: string]: unknown }): T;
    public abstract ui(name: string, args: { [key: string]: T | undefined }): T;
    public abstract action(name: string, args: { [key: string]: T | undefined }): T;

    public readonly getContext: T;

    private readonly _emptyUI: T;
    private readonly _errorUI: T;

    public readonly popScreen: T;
    public readonly popModalScreen: T;
    public readonly popAnyScreen: T;

    public readonly captureEnvironment: T;

    public readonly userProfileRow: T;

    constructor() {
        this.getContext = this.primitive("GetContext", {});

        this._emptyUI = this.ui("Empty", {});
        this._errorUI = this.label(this.localizedString("error"));

        this.popModalScreen = this.action("PopModalScreen", {});
        this.popScreen = this.action("PopScreen", {});
        this.popAnyScreen = this.action("PopAnyScreen", {});

        this.captureEnvironment = this.primitive("CaptureEnvironment", {});

        this.userProfileRow = this.lookupFunction("userProfileRow");
    }

    private valueOrString(v: T | string): T {
        if (typeof v === "string") {
            return this.constant(v);
        } else {
            return v;
        }
    }

    public get emptyUI(): T {
        return this._emptyUI;
    }

    public get errorUI(): T {
        return this._errorUI;
    }

    private call(callee: T, args: T[], once?: boolean): T {
        return this.primitive("Call", { callee, args, once: definedMap(once, this.constant) });
    }

    public getVariable(name: string): T {
        return this.primitive("GetVariable", { name });
    }

    public builtinCall(name: string, args: T[], once?: boolean): T {
        return this.call(this.primitive("LookupBuiltin", { name }), args, once);
    }

    public lookupFunction(name: string): T {
        return this.primitive("LookupFunction", { name });
    }

    public lookupGlobalVariable(name: string): T {
        return this.primitive("LookupGlobalVariable", { name });
    }

    public global(name: string, body: T): T {
        return this.primitive("Global", { name, body });
    }

    public function(contextName: string, argNames: string[], body: T): T {
        return this.primitive("Function", { contextName, argNames, body });
    }

    public object(propertyObject: { [key: string]: T | undefined }): T {
        const properties: [T, T][] = [];
        for (const k of Object.keys(propertyObject)) {
            const val = propertyObject[k];
            if (val !== undefined) {
                properties.push([this.constant(k), val]);
            }
        }
        return this.primitive("MakeObject", { properties });
    }

    public objectFromPairs(pairs: T): T {
        return this.primitive("MakeObjectFromPairs", { pairs });
    }

    public objectFromKeyAndValue(key: T, value: T): T {
        return this.objectFromPairs(this.list([this.list([key, value])]));
    }

    public functionCall(callable: string | T, args: (T | undefined)[]): T {
        if (typeof callable === "string") {
            callable = this.lookupFunction(callable);
        }
        return this.call(
            callable,
            args.map(a => (a !== undefined ? a : this.constant(null)))
        );
    }

    public list(items: ReadonlyArray<T>): T {
        return this.primitive("MakeList", { items });
    }

    public listNth(list: T, index: T | number): T {
        if (typeof index === "number") {
            index = this.constant(index);
        }
        return this.builtinCall("ListNth", [list, index]);
    }

    public listNthLast(list: T, index: T | number): T {
        if (typeof index === "number") {
            index = this.constant(index);
        }
        return this.builtinCall("ListNthLast", [list, index]);
    }

    public listLength(list: T): T {
        return this.builtinCall("ListLength", [list]);
    }

    public joinLists(one: T, two: T): T {
        return this.builtinCall("JoinLists", [one, two]);
    }

    public listTake(list: T, n: T): T {
        return this.builtinCall("ListTake", [list, n]);
    }

    public propertiesContainString(obj: T, properties: T, needle: T): T {
        return this.builtinCall("PropertiesContainString", [obj, properties, needle]);
    }

    public arrayContains(array: T, item: T): T {
        return this.builtinCall("ArrayContains", [array, item]);
    }

    public arraysOverlap(one: T, two: T): T {
        return this.builtinCall("ArraysOverlap", [one, two]);
    }

    public switch(value: T, cases: [T, T][], fallback: T): T {
        return this.primitive("Switch", {
            value,
            cases,
            fallback,
        });
    }

    public data = (v: unknown): T => {
        assert(v !== undefined);
        return this.primitive("Data", { data: v });
    };

    public constant = (v: unknown): T => {
        assert(v !== undefined);
        return this.primitive("Constant", { data: v });
    };

    public icon(icon: Icon | RemoteIcon): T {
        return this.constant(icon);
    }

    public let(bindings: readonly [string, T][], body: T): T {
        if (bindings.length === 0) return body;
        return this.primitive("Let", { bindings, body });
    }

    public captureLets(bindings: readonly [string, T][]): T {
        return this.let(bindings, this.captureEnvironment);
    }

    public hold(body: T): T {
        return this.primitive("Hold", { body });
    }

    public if(condition: T, consequent: T, alternative?: T): T {
        return this.primitive("If", { condition, then: consequent, else: alternative });
    }

    public and(a: T, b: T): T {
        return this.builtinCall("And", [a, b]);
    }

    public or(...xs: readonly T[]): T {
        assert(xs.length > 0);
        if (xs.length === 1) return xs[0];
        return this.builtinCall("Or", [xs[0], this.or(...xs.slice(1))]);
    }

    public not(s: T, fallback?: boolean): T {
        const args = [s];
        if (fallback !== undefined) {
            args.push(this.constant(fallback));
        }
        return this.builtinCall("Not", args);
    }

    public areIdentical(a: T, b: T): T {
        return this.builtinCall("AreIdentical", [a, b]);
    }

    public areEqual(a: T, b: T): T {
        return this.builtinCall("AreEqual", [a, b]);
    }

    public isLessThan(a: T, b: T): T {
        return this.builtinCall("IsLessThan", [a, b]);
    }

    public isGreaterOrEqualTo(a: T, b: T): T {
        return this.builtinCall("IsGreaterOrEqualTo", [a, b]);
    }

    public add(a: T, b: T): T {
        return this.builtinCall("Add", [a, b]);
    }

    public getNonTableProperty(property: string, objectData: T): T {
        assert(objectData !== undefined);
        return this.primitive("GetProperty", { object: objectData, property });
    }

    public getProperty(property: string, objectData: T, _table: TableGlideType | TableName): T {
        return this.getNonTableProperty(property, objectData);
    }

    public getDynamicProperty(property: T, objectData: T): T {
        return this.primitive("GetDynamicProperty", { object: objectData, property });
    }

    public bindContextToValue(value: T, context: T): T {
        return this.primitive("BindContextToValue", { value, context });
    }

    public clonePrimitive(source: T, defaultValue?: T): T {
        return this.primitive("ClonePrimitive", { source, defaultValue });
    }

    public clonePrimitives(source: T, properties: ReadonlyArray<[string, any]>): T {
        return this.primitive("ClonePrimitives", { source, properties });
    }

    public amendObject(
        obj: T,
        table: TableGlideType | TableName | undefined,
        keepProperties: ReadonlyArray<string>,
        newProperties: { [key: string]: T }
    ): T {
        const props: { [key: string]: T } = {};
        for (const p of keepProperties) {
            props[p] = table === undefined ? this.getNonTableProperty(p, obj) : this.getProperty(p, obj, table);
        }
        for (const p of Object.keys(newProperties)) {
            props[p] = newProperties[p];
        }
        return this.object(props);
    }

    public filter(list: T, predicate: T): T {
        return this.primitive("Filter", {
            list,
            predicate,
        });
    }

    public find(list: T, predicate: T): T {
        return this.primitive("Find", {
            list,
            predicate,
        });
    }

    public sort(list: T, key: T, order: T): T {
        return this.primitive("Sort", { key, order, list });
    }

    public group(list: T, key: T): T {
        return this.primitive("GroupList", { list, key });
    }

    public reverseList(list: T): T {
        return this.builtinCall("ReverseList", [list]);
    }

    public shuffle(list: T, uniqueID: string): T {
        return this.builtinCall("Shuffle", [list, this.constant(uniqueID)]);
    }

    public uniqueAndMapValues(list: T, mapper: T): T {
        return this.primitive("UniqueValues", { list, mapper });
    }

    public uniqueValues(list: T, mapper: T): T {
        return this.primitive("UniqueValues", { list, mapper, wholeItem: this.constant(true) });
    }

    public map(list: T, mapper: T): T {
        return this.primitive("MapList", { list, mapper });
    }

    public sum(list: T, mapper: T): T {
        return this.primitive("Sum", { list, number: mapper });
    }

    public localizedString(name: LocalizedStringKey | DevStringKey): T {
        return this.builtinCall("GetLocalizedString", [this.constant(name)]);
    }

    public formatLocalizedString(name: LocalizedStringKey, strings: ReadonlyArray<T>): T {
        return this.builtinCall("FormatLocalizedString", [this.constant(name), this.list(strings)]);
    }

    public formatPrice(price: T, currencyCode: T): T {
        return this.builtinCall("FormatPrice", [price, currencyCode]);
    }

    public isDefined(obj: T): T {
        return this.builtinCall("IsDefined", [obj]);
    }

    public isNotEmpty(obj: T): T {
        return this.builtinCall("IsNotEmpty", [obj]);
    }

    public ifIsDefined(testee: T, consequent: T, alternative?: T): T {
        return this.if(this.isDefined(testee), consequent, alternative);
    }

    public ifNonNull(ctx: T, child: T): T {
        return this.ifIsDefined(ctx, child, this.emptyUI);
    }

    public ifIsNotEmpty(ctx: T, child: T): T {
        return this.if(this.isNotEmpty(ctx), child, this.emptyUI);
    }

    public isNonEmptyArray(testee: T): T {
        return this.builtinCall("IsNonEmptyArray", [testee]);
    }

    public ifIsNonEmptyArray(testee: T, consequent: T, alternative: T): T {
        return this.if(this.isNonEmptyArray(testee), consequent, alternative);
    }

    public ifNonEmptyArray(ctx: T, child: T): T {
        return this.ifIsNonEmptyArray(ctx, child, this.emptyUI);
    }

    public areEmailsEqual(left: T, right: T): T {
        return this.builtinCall("AreEmailsEqual", [left, right]);
    }

    public withDefault(value: T, theDefault: T): T {
        return this.builtinCall("WithDefault", [value, theDefault]);
    }

    public getForwardee(value: T): T {
        return this.primitive("GetForwardee", { value });
    }

    public singletonItem(itemOrArray: T): T {
        return this.functionCall("singletonItem", [itemOrArray]);
    }

    public naiveNumberOfItems(list: T): T {
        return this.functionCall("naiveNumberOfItems", [list]);
    }

    public smartNumberOfItems(list: T, noneValue: T): T {
        return this.functionCall("smartNumberOfItems", [list, noneValue]);
    }

    public extractInitials(str: T): T {
        return this.withDefault(this.builtinCall("ExtractInitials", [str]), this.constant(""));
    }

    public convertToNumber(v: T): T {
        return this.builtinCall("ConvertToNumber", [v]);
    }

    public convertToBoolean(v: T): T {
        return this.builtinCall("ConvertToBoolean", [v]);
    }

    public convertToString(v: T): T {
        return this.builtinCall("ConvertToString", [v]);
    }

    public convertToLocaleAgnosticString(v: T): T {
        return this.builtinCall("ConvertToLocaleAgnosticString", [v]);
    }

    public stringEmpty(v: T): T {
        return this.builtinCall("StringEmpty", [v]);
    }

    public replaceStrings(template: T, replacements: readonly T[]): T {
        return this.builtinCall("ReplaceStrings", [template, this.list(replacements)]);
    }

    public parseUserDateTime(v: T): T {
        return this.builtinCall("ParseUserDateTime", [v]);
    }

    public startOfDay(v: T): T {
        return this.builtinCall("StartOfDay", [v]);
    }

    public classifyLink(link: T): T {
        return this.builtinCall("ClassifyLink", [link]);
    }

    public extractIframe(link: T): T {
        return this.builtinCall("ExtractIframe", [link]);
    }

    public shortenLink(link: T): T {
        return this.builtinCall("ShortenLink", [link]);
    }

    public getLastURLPathComponent(link: T): T {
        return this.builtinCall("GetLastURLPathComponent", [link]);
    }

    public isPropertyDefined(propertyName: string, ctx: T, table: TableGlideType | TableName): T {
        return this.builtinCall("IsDefined", [this.getProperty(propertyName, ctx, table)]);
    }

    public fetchTableRows(dataStoreName: string, tableName: T | string, forBuilder: boolean): T {
        return this.builtinCall("FetchTableRows", [
            this.constant(dataStoreName),
            this.valueOrString(tableName),
            this.constant(forBuilder),
        ]);
    }

    public fetchComments(topic: T, sortOrder: T): T {
        return this.builtinCall("FetchComments", [topic, sortOrder]);
    }

    public lookupGeoAddress(address: T, quotaKey: string) {
        return this.builtinCall("LookupGeoAddress", [address, this.constant(quotaKey)]);
    }

    public sequence(actions: T[]): T {
        if (actions.length === 0) {
            return this.noAction();
        }
        if (actions.length === 1) {
            return actions[0];
        }
        if (actions.length <= 4) {
            const [first, second, third, fourth] = actions;
            return this.action("Sequence", { first, second, third, fourth });
        }
        return this.action("Sequence", {
            first: actions[0],
            second: actions[1],
            third: actions[2],
            fourth: this.sequence(actions.slice(3)),
        });
    }

    public label(text: T): T {
        return this.ui("Label", { text });
    }

    public holdContainer(child: T): T {
        return this.ui("HoldContainer", { child });
    }

    public holdAction(action: T): T {
        return this.action("HoldAction", { action });
    }

    public column(children: ReadonlyArray<T>, style?: ColumnStyle): T {
        return this.ui("Column", {
            children: this.list(children),
            style: definedMap(style, this.constant),
        });
    }

    public smallList(list: T, item: T, caption: T | undefined, allowWrapping: T | undefined): T {
        return this.ui("SmallList", { list, item, caption, allowWrapping });
    }

    public adaptiveListItem(
        title: T | undefined,
        subtitle: string | T | undefined,
        caption: string | T | undefined,
        image: T | undefined,
        icon: T | undefined,
        fallbackInitials: T | undefined,
        onTap: T | undefined,
        onDropImage: T | undefined,
        actionSource: ActionSource | undefined,
        flags: T | ListItemFlags | undefined,
        accessoryView: T | undefined,
        accessoryPosition: T | ListItemAccessoryPosition | undefined,
        itemID: T | undefined
    ): T {
        if (typeof subtitle === "string") {
            subtitle = this.constant(subtitle);
        }
        if (subtitle !== undefined) {
            subtitle = this.withDefault(subtitle, this.constant(""));
        }

        if (typeof caption === "string") {
            caption = this.constant(caption);
        }

        if (typeof flags === "number") {
            flags = this.constant(flags);
        }

        if (typeof accessoryPosition === "string") {
            accessoryPosition = this.constant(accessoryPosition);
        }

        return this.ui("AdaptiveListItem", {
            title,
            subtitle,
            caption,
            image,
            icon,
            fallbackInitials,
            onTap,
            onDropImage,
            actionSource: definedMap(actionSource, this.constant),
            flags,
            accessoryView,
            accessoryPosition,
            itemID,
        });
    }

    public imageAccessory(icon: T): T {
        return this.ui("ImageAccessory", { icon });
    }

    public gridItem(
        title: T | undefined,
        subtitle: T | undefined,
        image: T | undefined,
        size: ImageAspectRatio,
        fallbackInitials: T | undefined,
        onTap: T | undefined,
        onDropImage: T | undefined
    ): T {
        return this.ui("GridItem", {
            title,
            subtitle,
            image,
            size: this.constant(size),
            fallbackInitials,
            onTap,
            onDropImage,
        });
    }

    public searchBar(text: T, placeholder: T, searchActiveFlag: T | undefined): T {
        return this.ui("SearchBar", { placeholder, text, searchActive: searchActiveFlag });
    }

    public button(
        text: string | T,
        appearance: Appearance,
        mood: Mood | undefined,
        floatingIcon: Icon | undefined,
        onTap: T,
        confirm?: boolean,
        confirmStyle?: string,
        confirmTitle?: T,
        confirmDesc?: T,
        cancelText?: T,
        acceptText?: T
    ): T {
        return this.ui("Button", {
            text: typeof text === "string" ? this.constant(text) : text,
            appearance: this.constant(appearance),
            mood: definedMap(mood, this.constant),
            floatingIcon: definedMap(floatingIcon, this.constant),
            onTap,
            confirm: definedMap(confirm, this.constant),
            confirmStyle: definedMap(confirmStyle, this.constant),
            confirmTitle,
            confirmDesc,
            cancelText,
            acceptText,
        });
    }

    public avatarOverlay(image: T | undefined, caption: T | undefined, textSize: T | undefined): T | undefined {
        if (image === undefined && caption === undefined) return undefined;
        return this.ui("AvatarOverlay", { image, caption, textSize });
    }

    public userProfile(
        name: T | undefined,
        email: T | undefined,
        image: T | undefined,
        onChange: T | undefined,
        isEnabled: T | undefined,
        removeBranding: boolean
    ): T {
        return this.ui("UserProfile", {
            title: name,
            subtitle: email,
            image,
            onChange,
            isEnabled,
            removeBranding: this.constant(removeBranding),
        });
    }

    public caption(s: string | undefined): T | undefined {
        return definedMap(undefinedIfEmptyString(s), this.constant);
    }

    public noAction(): T {
        return this.action("NoAction", {});
    }

    public actionIfContextDefined(action: T): T {
        return this.ifIsDefined(this.getContext, action, this.noAction());
    }

    public dropImageAction(
        tableName: string,
        propertyName: string,
        rowIndexOrRowID: RowIndexOrRowID<T>,
        imageURLToSet: T
    ): T {
        return this.action("DropImage", {
            tableName: this.constant(tableName),
            columnName: this.constant(propertyName),
            ...rowIndexOrRowID,
            imageURLToSet,
        });
    }

    public showToast(message: T, isSuccess: boolean): T {
        return this.action("ShowFormSuccessToast", { message, isSuccess: this.constant(isSuccess) });
    }

    public playSound(sound: T) {
        return this.action("PlaySound", { sound });
    }

    public screenContext(screenData: T, additionalProperties?: { [key: string]: T }): T {
        return this.let(
            [["screenData", screenData]],
            this.if(
                this.isNotEmpty(this.getVariable("screenData")),
                this.object({ data: this.getVariable("screenData"), ...additionalProperties })
            )
        );
    }

    public pushBuilderScreen(name: string, ctx: T, title: T | undefined, itemID: T | undefined): T {
        return this.action("PushBuilderScreen", {
            name: this.constant(name),
            ctx,
            title,
            itemID,
        });
    }

    public pushModalBuilderScreen(
        name: string,
        ctx: T,
        title: T | undefined,
        forEditComponent: boolean | "offline-action-queue",
        reallyNeedsOnline: boolean
    ): T {
        return this.action("PushModalBuilderScreen", {
            name: this.constant(name),
            ctx,
            title,
            forEditComponent: this.constant(forEditComponent),
            reallyNeedsOnline: this.constant(reallyNeedsOnline),
        });
    }

    public pushMenuScreen(menuID: string, primaryKey: T, defaultScreenName: string, ctx: T): T {
        return this.action("PushMenuScreen", {
            menuID: this.constant(menuID),
            primaryKey,
            defaultScreenName: this.constant(defaultScreenName),
            ctx,
        });
    }

    public postComment(topic: T): T {
        return this.action("PostComment", { topic });
    }

    public openLink(url: T): T {
        return this.action("OpenLink", { url });
    }

    public openMap(address: T): T {
        return this.action("OpenMap", { address });
    }

    public sendEmail(to: T, subject?: T, body?: T, cc?: T, bcc?: T): T {
        return this.action("SendEmail", { to, subject, body, cc, bcc });
    }

    public phoneCall(number: T): T {
        return this.functionCall("phoneCall", [number]);
    }

    public textMessage(number: T, body: T | undefined): T {
        const args = [number];
        if (body !== undefined) {
            args.push(body);
        } else {
            args.push(this.constant(null));
        }
        return this.functionCall("textMessage", args);
    }

    public showShareSheet(url: T | undefined, message: T | undefined): T {
        return this.action("ShowShareSheet", { url, message });
    }

    public set(lhs: T, rhs: T): T {
        return this.action("Set", { lhs, rhs });
    }

    public removeItemFromParent(item: T): T {
        return this.action("RemoveItemFromParent", { item });
    }

    // FIXME: This kinda belongs in `ComponentBuilder`
    public actionWithUserSpecificData(onSuccess: T, onAbort: T): T {
        // FIXME: We shouldn't duplicate `onSuccess` here.
        return this.if(
            this.builtinCall("HaveUserSpecificData", []),
            onSuccess,
            this.pushModalBuilderScreen(
                emailAuthScreenName,
                this.object({
                    onSuccess: this.sequence([this.popModalScreen, onSuccess]),
                    onAbort: this.sequence([onAbort, this.popModalScreen]),
                }),
                undefined,
                true,
                true
            )
        );
    }

    public primitiveDiode(inputValue: T, defaultValue: T | undefined): T {
        const args = [inputValue];
        if (defaultValue !== undefined) {
            args.push(defaultValue);
        }
        return this.builtinCall("PrimitiveDiode", args, true);
    }

    public canWriteRow(rowIndexOrRowID: RowIndexOrRowID<T>): T {
        if (hasOwnProperty(rowIndexOrRowID, "keyColumnValue")) {
            return this.isDefined(rowIndexOrRowID.keyColumnValue);
        } else {
            return this.isDefined(rowIndexOrRowID.rowIndex);
        }
    }

    public setColumnsInRow(
        tableName: T | string,
        rowIndexOrRowID: RowIndexOrRowID<T>,
        columnValues: T,
        debounce: boolean
    ): T {
        return this.action("SetColumnsInRow", {
            tableName: this.valueOrString(tableName),
            ...rowIndexOrRowID,
            columnValues,
            setUnderlyingData: this.constant(true),
            debounce: this.constant(debounce),
        });
    }

    public setColumnsInRowForColumnValues(
        columnValues: { [key: string]: T },
        tableName: T | string,
        rowIndexOrRowID: RowIndexOrRowID<T>,
        debounce: boolean
    ): { action: T; isEnabled: T } {
        const action = this.setColumnsInRow(tableName, rowIndexOrRowID, this.object(columnValues), debounce);
        return { action, isEnabled: this.canWriteRow(rowIndexOrRowID) };
    }

    public setColumnInRowForProperty(
        propertyName: T | string,
        propertyValue: T,
        tableName: T | string,
        rowIndexOrRowID: RowIndexOrRowID<T>,
        debounce: boolean
    ): { action: T; isEnabled: T } {
        let columnValues: T;
        if (typeof propertyName === "string") {
            columnValues = this.object({ [propertyName]: propertyValue });
        } else {
            columnValues = this.objectFromKeyAndValue(propertyName, propertyValue);
        }
        const action = this.setColumnsInRow(tableName, rowIndexOrRowID, columnValues, debounce);
        return { action, isEnabled: this.canWriteRow(rowIndexOrRowID) };
    }

    public addRowToTable(
        dataStoreName: string,
        tableName: TableName,
        columnValues: T,
        columnNames: T | undefined,
        onWriteSuccess: T | undefined
    ): T {
        return this.action("AddRowToTable", {
            tableName: this.constant(tableName.name),
            columnValues,
            columnNames,
            addToData: this.constant(true),
            onWriteSuccess,
            dataStoreName: this.constant(dataStoreName),
        });
    }

    public deleteRow(tableName: TableName, rowIndexOrRowID: RowIndexOrRowID<T>): T {
        return this.action("DeleteRow", {
            tableName: this.constant(tableName.name),
            setUnderlyingData: this.constant(true),
            ...rowIndexOrRowID,
        });
    }

    public addComputedColumnsToData(tableName: TableName | T, data: T): T {
        let tableNameSerialized: T;
        if (isTableName(tableName)) {
            tableNameSerialized = this.constant(tableName.name);
        } else {
            tableNameSerialized = tableName;
        }
        return this.builtinCall("AddComputedColumnsToData", [tableNameSerialized, data], true);
    }

    public verifiedEmailAddress(): T {
        return this.lookupGlobalVariable(GlobalVariableNames.VerifiedEmailAddress);
    }

    public realEmailAddress(): T {
        return this.lookupGlobalVariable(GlobalVariableNames.RealEmailAddress);
    }

    public fallbackUsername(): T {
        return this.builtinCall("FallbackUsername", []);
    }

    public specialValue(kind: SpecialValueKind): T {
        switch (kind) {
            case SpecialValueKind.Timestamp:
                return this.builtinCall("CurrentTimestamp", []);

            case SpecialValueKind.VerifiedEmailAddress:
                return this.verifiedEmailAddress();

            case SpecialValueKind.RealEmailAddress:
                return this.realEmailAddress();

            case SpecialValueKind.UniqueIdentifier:
                return this.builtinCall("UniqueIdentifier", []);

            case SpecialValueKind.ActionSource:
                return this.primitive("ActionSource", {});

            case SpecialValueKind.CurrentURL:
                return this.builtinCall("CurrentURL", []);

            case SpecialValueKind.ClearColumn:
                return this.constant("");

            default:
                return assertNever(kind);
        }
    }

    public currentLocation(): T {
        return this.builtinCall("CurrentLocation", []);
    }
}

export class SerializedElementBuilder extends ElementBuilder<SerializedComponent> {
    public primitive(name: string, args: { [key: string]: unknown }): SerializedComponent {
        return { $kind: name, ...args };
    }

    public ui(name: string, args: { [key: string]: SerializedComponent | undefined }): SerializedComponent {
        return { $kind: name, ...args };
    }

    public action(name: string, args: { [key: string]: SerializedComponent | undefined }): SerializedComponent {
        return { $kind: name, ...args };
    }
}

export type LetBinding = [string, SerializedComponent];

export function fetchLinkColumn(
    b: SerializedElementBuilder,
    column: TableColumn,
    columnCtx: SerializedComponent,
    forBuilder: boolean
): SerializedComponent {
    if (isComputedColumn(column)) {
        return columnCtx;
    }

    if (column.type.kind === "array") {
        return b.builtinCall("FetchMultipleLinkRows", [columnCtx, b.constant(forBuilder)]);
    } else {
        return b.builtinCall("FetchSingleLinkRow", [columnCtx, b.constant(forBuilder)]);
    }
}
